import {useCallback, useRef} from 'react';
import {throttle, useSyncRef} from '@chekin/core';
import type {FaceDetectionSignal} from '../../faceDetection';
import {useLivenessStore, useProgressStore} from '../store';
import {LIVENESS_BLINK_REFRACTORY_MS, LIVENESS_BLINK_TARGET} from '../constants';

const LEFT_THRESHOLD = 1.2;
const RIGHT_THRESHOLD = -1.2;
const UP_THRESHOLD = 0.3;
const NORMAL_OFFSET_Y = 1.2;

const PROGRESS_THROTTLE = 150;

export function useLivenessChallengeTracking() {
  const currentChallenge = useLivenessStore(state => state.currentChallenge);
  const currentChallengeRef = useSyncRef(currentChallenge);
  const registerBlink = useLivenessStore(state => state.registerBlink);
  const wasBlinkingRef = useRef(false);
  const lastCountedBlinkRef = useRef(0);
  const setProgress = useProgressStore(state => state.setProgress);
  const setProgressRef = useSyncRef(setProgress);

  const throttledSetProgress = useRef(
    throttle((progress: number) => {
      setProgressRef.current(progress);
    }, PROGRESS_THROTTLE),
  ).current;

  const countBlink = useCallback(
    (signal: FaceDetectionSignal) => {
      const wasBlinking = wasBlinkingRef.current;
      wasBlinkingRef.current = Boolean(signal.isBlinking);

      const blinked =
        signal.source === 'client'
          ? Boolean(signal.didBlink)
          : Boolean(signal.isBlinking) && !wasBlinking;
      if (!blinked) return;

      const now = Date.now();
      if (now - lastCountedBlinkRef.current < LIVENESS_BLINK_REFRACTORY_MS) return;
      lastCountedBlinkRef.current = now;

      if (useLivenessStore.getState().blinkCount >= LIVENESS_BLINK_TARGET) return;
      registerBlink();
    },
    [registerBlink],
  );

  const onSignal = useCallback(
    (signal: FaceDetectionSignal) => {
      const challenge = currentChallengeRef.current;

      if (challenge === 'BLINK') {
        countBlink(signal);
        const {blinkCount} = useLivenessStore.getState();
        throttledSetProgress((100 * blinkCount) / LIVENESS_BLINK_TARGET);
        return;
      }

      wasBlinkingRef.current = false;

      if (signal.source === 'client') {
        if (signal.offsetX === undefined || signal.offsetY === undefined) {
          throttledSetProgress(0);
          return;
        }

        let progress = 0;
        if (challenge === 'TURN_LEFT') {
          progress = (100 * signal.offsetX) / LEFT_THRESHOLD;
        } else if (challenge === 'TURN_RIGHT') {
          progress = (100 * signal.offsetX) / RIGHT_THRESHOLD;
        } else if (challenge === 'TURN_UP') {
          progress =
            (100 * (signal.offsetY - NORMAL_OFFSET_Y)) / (UP_THRESHOLD - NORMAL_OFFSET_Y);
        }
        progress = Math.max(0, Math.min(100, progress));
        throttledSetProgress(progress);
        return;
      }

      const {direction, movePercent} = signal;
      if (movePercent !== null && movePercent !== undefined && challenge) {
        const dir = (direction || '').toUpperCase();
        const shouldUpdateProgress =
          (challenge === 'TURN_LEFT' && dir === 'LEFT') ||
          (challenge === 'TURN_RIGHT' && dir === 'RIGHT') ||
          (challenge === 'TURN_UP' && dir === 'UP') ||
          dir === 'CENTER';

        if (shouldUpdateProgress) {
          setProgressRef.current(movePercent);
        }
      }
    },
    [currentChallengeRef, countBlink, throttledSetProgress, setProgressRef],
  );

  return {onSignal};
}
